import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useCalendarDataContext } from '../contexts/CalendarDataContext';
import { useTranslation } from '../i18n';
import './CalendarSwitcher.scss';

/**
 * CalendarSwitcher component - dropdown to switch between calendars
 * Only shown for authenticated users
 */
function CalendarSwitcher({ onManageCalendars }) {
  const { isAuthenticated } = useAuth();
  const { calendars, currentCalendar, switchCalendar, isLoading } = useCalendarDataContext();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Close on escape
  useEffect(() => {
    function handleEscape(event) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  // Only hide during initial load when we have no calendars yet
  // Don't hide during subsequent data loading operations
  const hasCalendars = calendars?.owned?.length > 0 || calendars?.shared?.length > 0;
  if (!isAuthenticated || (isLoading && !hasCalendars)) {
    return null;
  }

  const allCalendars = [
    ...(calendars?.owned || []),
    ...(calendars?.shared || []),
  ];

  if (allCalendars.length === 0) {
    return null;
  }

  const handleSelect = (calendarId) => {
    switchCalendar(calendarId);
    setIsOpen(false);
  };

  return (
    <div className="calendar-switcher" ref={dropdownRef}>
      <button
        className="calendar-switcher__trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span
          className="calendar-switcher__color"
          style={{ backgroundColor: currentCalendar?.color || '#4285f4' }}
        />
        <span className="calendar-switcher__name">
          {currentCalendar?.name || t('calendar.selectCalendar')}
        </span>
        <span className="calendar-switcher__arrow">{isOpen ? '\u25B2' : '\u25BC'}</span>
      </button>

      {isOpen && (
        <div className="calendar-switcher__dropdown" role="listbox">
          <div className="calendar-switcher__section">
            <span className="calendar-switcher__section-label">
              {t('calendar.myCalendars')}
            </span>
            {(calendars?.owned || []).map((cal) => (
              <button
                key={cal.id}
                className={`calendar-switcher__option ${
                  cal.id === currentCalendar?.id ? 'calendar-switcher__option--selected' : ''
                }`}
                onClick={() => handleSelect(cal.id)}
                role="option"
                aria-selected={cal.id === currentCalendar?.id}
              >
                <span
                  className="calendar-switcher__color"
                  style={{ backgroundColor: cal.color }}
                />
                <span className="calendar-switcher__option-name">{cal.name}</span>
                {cal.isDefault && (
                  <span className="calendar-switcher__badge">{t('calendar.default')}</span>
                )}
              </button>
            ))}
          </div>

          {(calendars?.shared || []).length > 0 && (
            <div className="calendar-switcher__section">
              <span className="calendar-switcher__section-label">
                {t('calendar.sharedWithMe')}
              </span>
              {(calendars?.shared || []).map((cal) => (
                <button
                  key={cal.id}
                  className={`calendar-switcher__option ${
                    cal.id === currentCalendar?.id ? 'calendar-switcher__option--selected' : ''
                  }`}
                  onClick={() => handleSelect(cal.id)}
                  role="option"
                  aria-selected={cal.id === currentCalendar?.id}
                >
                  <span
                    className="calendar-switcher__color"
                    style={{ backgroundColor: cal.color }}
                  />
                  <span className="calendar-switcher__option-name">{cal.name}</span>
                  <span className="calendar-switcher__permission">
                    {cal.permission === 'edit' ? t('calendar.canEdit') : t('calendar.viewOnly')}
                  </span>
                </button>
              ))}
            </div>
          )}

          <div className="calendar-switcher__actions">
            <button
              className="calendar-switcher__manage"
              onClick={() => {
                setIsOpen(false);
                onManageCalendars?.();
              }}
            >
              {t('calendar.manageCalendars')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default CalendarSwitcher;
